import { useEffect, useMemo, useState } from 'react';

import { useLayoutContext } from '@ott/layout-context';
import { useMessage } from '@ott/message';

import {
  Email,
  Facebook,
  Link,
  Reddit,
  Telegram,
  Twitter,
  WhatsApp,
  Done,
} from 'src/components/elements/Icons';
import {
  getFbShareUrl,
  getRedditShareUrl,
  getTelegramShareUrl,
  getTwitterShareUrl,
  getUserPromoLink,
  getWhatsAppShareUrl,
} from 'src/components/pages/loyalty/ReferralPage/blocks/Invite/helpers/shareUrlHelper';

import useIsWebview from 'src/hooks/useIsWebview';
import { BubbleType } from 'src/types';

import { getXYForTwoCircles, getXYForThreeCircles } from '../../helpers/positionGetter';

export const bubbleData: () => BubbleType[] = () => {
  const { isDesktopLayout, isTabletLayout, isMobileLayout } = useLayoutContext();
  const [k, setK] = useState(1);
  const [center, setCenter] = useState({ x: 600, y: 500 });
  const [userPromoLink, setUserPromoLink] = useState('');
  const isWebview = useIsWebview();
  const toastLabel = 'Link copied to clipboard';

  useEffect(() => {
    getUserPromoLink().then((newLink) => setUserPromoLink(newLink));
  }, []);

  useEffect(() => {
    if (isDesktopLayout) {
      setK(() => 1);
    } else if (isTabletLayout) {
      setK(() => 1 / 1.5);
      setCenter({ x: 170, y: 400 });
    } else {
      setK(() => 1 / 2);
      setCenter({ x: 130, y: 300 });
    }
  }, [isDesktopLayout, isTabletLayout]);

  const openLink = (link: string) => {
    if (!link) {
      return null;
    }
    return () => window.open(link, '_blank');
  };

  const replyX = 0;
  const replyY = 0;
  const replyRadius = 205 * k;
  const ms = useMessage();

  const replyOnClick = async () => {
    if (isWebview && window.NativeAndroidInterface?.copyToClipboard) {
      window.NativeAndroidInterface?.copyToClipboard(userPromoLink, toastLabel);
    } else {
      await navigator.clipboard
        .writeText(userPromoLink)
        .then(() => {
          if (!userPromoLink) {
            throw null;
          }
          ms.open(toastLabel, {
            icon: Done(),
            style: { backgroundColor: '#3CC763', color: 'white' },
          });
        })
        .catch(() => {
          ms.open('Something went wrong', {
            style: { backgroundColor: '#e74c3c', color: 'white' },
          });
        });
    }
  };

  const tgRadius = 125 * k;
  const tgAngle = isMobileLayout ? -40 : 0;
  const { x: tgX, y: tgY } = useMemo(
    () => getXYForTwoCircles(replyRadius, tgRadius, tgAngle),
    [replyRadius, tgRadius, tgAngle]
  );

  const mailRadius = 149 * k;
  const mailAngle = 135;
  const { x: mailX, y: mailY } = useMemo(() => {
    return isMobileLayout
      ? getXYForThreeCircles(
          tgX,
          replyX,
          tgY,
          replyY,
          tgRadius + mailRadius,
          replyRadius + mailRadius
        )
      : getXYForTwoCircles(tgRadius, mailRadius, mailAngle, tgX, tgY);
  }, [tgX, replyX, tgY, replyY, tgRadius, replyRadius, mailRadius, mailAngle]);

  const waRadius = 78 * k;
  const waK = -1;
  const { x: waX, y: waY } = useMemo(() => {
    return isMobileLayout
      ? getXYForThreeCircles(
          replyX,
          mailX,
          replyY,
          mailY,
          replyRadius + waRadius,
          mailRadius + waRadius,
          waK
        )
      : getXYForThreeCircles(
          replyX,
          tgX,
          replyY,
          tgY,
          replyRadius + waRadius,
          tgRadius + waRadius,
          waK
        );
  }, [replyX, mailX, replyY, mailX, replyRadius, mailRadius, waRadius, tgX, tgY, tgRadius]);

  const redditRadius = 114 * k;
  const redditK = -1;
  const { x: redditX, y: redditY } = useMemo(() => {
    return isMobileLayout
      ? getXYForThreeCircles(
          replyX,
          waX,
          replyY,
          waY,
          replyRadius + redditRadius,
          waRadius + redditRadius,
          redditK
        )
      : getXYForThreeCircles(tgX, waX, tgY, waY, tgRadius + redditRadius, waRadius + redditRadius);
  }, [replyX, waX, replyY, waY, redditRadius, waRadius, redditRadius, redditK, tgX, tgY, tgRadius]);

  const fbRadius = 125 * k;
  const fbAngle = isMobileLayout ? -125 : 90;
  const { x: fbX, y: fbY } = useMemo(
    () => getXYForTwoCircles(replyRadius, fbRadius, fbAngle),
    [replyRadius, fbRadius, fbAngle]
  );

  const twRadius = 149 * k;
  const { x: twX, y: twY } = useMemo(() => {
    return isMobileLayout
      ? getXYForThreeCircles(
          redditX,
          fbX,
          redditY,
          fbY,
          redditRadius + twRadius,
          fbRadius + twRadius
        )
      : getXYForThreeCircles(replyX, fbX, replyY, fbY, replyRadius + twRadius, fbRadius + twRadius);
  }, [redditX, fbX, redditY, fbY, twRadius, fbRadius, twRadius, replyX, replyY, replyRadius]);

  return [
    {
      id: 1,
      radius: replyRadius,
      x: replyX + center.x,
      y: replyY + center.y,
      icon: Link(),
      onClick: replyOnClick,
      webview: true,
    },
    {
      id: 2,
      radius: fbRadius,
      x: fbX + center.x,
      y: fbY + center.y,
      icon: Facebook(),
      onClick: openLink(getFbShareUrl(userPromoLink)),
      webview: false,
    },
    {
      id: 3,
      radius: twRadius,
      x: twX + center.x,
      y: twY + center.y,
      icon: Twitter(),
      onClick: openLink(getTwitterShareUrl(userPromoLink)),
      webview: false,
    },
    {
      id: 4,
      radius: tgRadius,
      x: tgX + center.x,
      y: tgY + center.y,
      icon: Telegram(),
      onClick: openLink(getTelegramShareUrl(userPromoLink)),
      webview: true,
    },
    {
      id: 5,
      radius: waRadius,
      x: waX + center.x,
      y: waY + center.y,
      icon: WhatsApp(),
      onClick: openLink(getWhatsAppShareUrl(userPromoLink)),
      webview: true,
    },
    {
      id: 6,
      radius: redditRadius,
      x: redditX + center.x,
      y: redditY + center.y,
      icon: Reddit(),
      onClick: openLink(getRedditShareUrl(userPromoLink)),
      webview: false,
    },
    {
      id: 7,
      radius: mailRadius,
      x: mailX + center.x,
      y: mailY + center.y,
      icon: Email(),
      onClick: () => window.open(`mailto:?subject=${encodeURIComponent(userPromoLink)}`),
      webview: true,
    },
  ];
};
